import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { getDisabledFields } from '../../../../constants/tanFormDisabled'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import dayjs from 'dayjs'
import {COUNTRY_LIST} from "../../../../constants"

/**
 * Field label mapping for new forms (Form 140/144).
 * Key: label text used in legacy form, Value: new label for Form 140/144
 */
const NEW_FORM_LABELS = {
  'Financial Year*': 'Tax year*',
  'Name of Premises/Building': 'Post Office',
  'Area/Location': 'Area/locality',
  'Town/District/City': 'District',
  'Town/City/District': 'District',
  'Pin Code*': 'PIN/ZIP Code*',
  'Pin Code': 'PIN/ZIP Code',
  'STD Code': 'Country Code*',
  'Telephone No.': 'Number*',
  'Has regular statement filed for Form 26Q for earlier period': 'Has the statement been filled earlier for this Quarter',
  'Receipt No. of earlier statement filed for Form 26Q': 'Receipt Number of Original Return',
  'Has regular statement for Form 27Q filed for earlier period': 'Has the statement been filled earlier for this Quarter',
  'Receipt No. of earlier statement filed for Form 27Q': 'Receipt Number of Original Return',
}

/**
 * Fields to hide from Returns form for new forms (Form 140/144).
 * These are "Removed from Return - Retain in TAN Master" per mapping sheet.
 * Matched by formData field key.
 */
const HIDDEN_FIELDS_NEW_FORM = [
  'revisedReturn',
  'branchOrDivision',
  'alternateEmail',
  'alternateStdCode',
  'alternateTelephoneNo',
  'paoCode',
  'ddoCode',
  'paoRegistrationNo',
  'ddoRegistrationNo',
  'addressChangedSinceLastReturn',
  // Responsible Person section
  'responsiblePersonAlternateEmail',
  'responsiblePersonAlternateStdCode',
  'responsiblePersonAlternateTelephoneNo',
  'responsiblePersonMobileNo',
  'addressChangedSinceLastReturnRP',
  'responsiblePersonEmailAlt'
]

function ReturnsForm({ editMode, formHook, formVersion }) {
  const { formData, errors, handleChange, handleFieldBlur, touched } = formHook
  const disabledFields = getDisabledFields(formData.deductorType)
  const isNewForm = formVersion === 'new'

  console.log(isNewForm,"isNewForm",errors,"errors");
  

  /**
   * Resolves the display label based on form version.
   * For new forms, returns the mapped new label if available.
   */
  const getLabel = (legacyLabel) => {
    if (!isNewForm) return legacyLabel
    return NEW_FORM_LABELS[legacyLabel] || legacyLabel
  }

  /**
   * Checks if a field should be hidden for new forms.
   * @param {string} fieldKey - the formData key for the field
   * @returns {boolean}
   */
  const isHiddenField = (fieldKey) => {
    if (!isNewForm) return false
    return HIDDEN_FIELDS_NEW_FORM.includes(fieldKey)
  }

  /**
   * Style object to hide a field's parent Box when it should be removed for new forms.
   * Uses display:none to completely remove from layout flow (no empty space).
   * Apply as: sx={{ ...hiddenStyle('fieldKey') }}
   */
  const hiddenStyle = (fieldKey) => {
    if (!isHiddenField(fieldKey)) return {}
    return { display: 'none !important' }
  }

  const getTextFieldStyles = () => ({
    '& .MuiOutlinedInput-root': {
      height: '40px',
      backgroundColor: editMode ? '#fff' : '#f5f5f5',
      borderRadius: 'unset',
      ...(editMode
        ? {
          '& fieldset': {
            borderColor: '#d0d0d0',
          },
          '&:hover fieldset': {
            borderColor: '#a0a0a0',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'var(--forbrand-color)',
            borderWidth: '1px',
          },
          '&.Mui-error fieldset': {
            borderColor: 'var(--forbrand-error)',
          },
          '&.Mui-error:hover fieldset': {
            borderColor: 'var(--forbrand-error)',
          },
          '&.Mui-error.Mui-focused fieldset': {
            borderColor: 'var(--forbrand-error)',
          },
        }
        : {
          '& fieldset': { border: 'none' },
        }),
    },
  })

  const getSelectStyles = (disabled = false) => ({
    height: '40px',
    fontSize: '14px',
    backgroundColor: disabled || !editMode ? '#f5f5f5' : '#fff',
    borderRadius: 'unset',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: disabled || !editMode ? 'transparent' : '#d0d0d0',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: disabled || !editMode ? 'transparent' : '#a0a0a0',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: disabled || !editMode ? 'transparent' : 'var(--forbrand-color)',
      borderWidth: '1px',
    },
    '&.Mui-disabled': {
      backgroundColor: '#f5f5f5',
    },
  })

  const getConditionalTextFieldStyles = (isDisabled) => ({
    '& .MuiOutlinedInput-root': {
      height: '40px',
      fontSize: '14px',
      backgroundColor: isDisabled ? '#f5f5f5' : '#fff',
      borderRadius: 'unset',
      '& fieldset': {
        borderColor: isDisabled ? 'transparent' : '#d0d0d0',
      },
      '&:hover fieldset': {
        borderColor: isDisabled ? 'transparent' : '#a0a0a0',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDisabled ? 'transparent' : 'var(--forbrand-color)',
        borderWidth: '1px',
      },
      '&.Mui-disabled': {
        backgroundColor: '#f5f5f5',
      },
    },
  })

  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year()
    return Array.from({ length: 7 }, (_, i) => {
      const start = currentYear - i
      const end = (start + 1).toString().slice(-2)
      return { label: `${start}-${end}`, value: `${start}-${end}` }
    })
  }, [])

  const FY_QUARTERS = [
    { label: 'Q1', value: 'Q1', startMonth: 4 },
    { label: 'Q2', value: 'Q2', startMonth: 7 },
    { label: 'Q3', value: 'Q3', startMonth: 10 },
    { label: 'Q4', value: 'Q4', startMonth: 1 },
  ]

  const getQuarterOptionsByFY = selectedFY => {
    const today = new Date()
    const month = today.getMonth() + 1
    const year = today.getFullYear()

    // Parse FY
    const [fyStart, fyEnd] = selectedFY.split('-').map(Number)

    // Determine current FY
    const currentFYStart = month >= 4 ? year : year - 1
    const currentFYEnd = currentFYStart + 1

    // 🔹 Future FY → no quarters
    if (fyStart > currentFYStart) return []

    // 🔹 Past FY → all quarters
    if (fyEnd < currentFYEnd) {
      return FY_QUARTERS.map(({ label, value }) => ({ label, value }))
    }

    // 🔹 Current FY → show quarters that HAVE STARTED
    return FY_QUARTERS.filter(q => {
      // Q4 is Jan–Mar of next calendar year
      if (q.value === 'Q4') return month >= 1
      return month >= q.startMonth
    }).map(({ label, value }) => ({ label, value }))
  }


  const quarterOptions = getQuarterOptionsByFY(formData?.financialYear)

  return (
    <>
      {/* Particulars Of Statement */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontWeight: 600,
            fontSize: { xs: '16px', sm: '18px' },
          }}
        >
          {isNewForm ? '1. Part A.' : '1. Particulars Of Statement'}
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Tax Deduction and Collection Account No. (TAN)*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.tanNumber}
              error={!!errors.tanNumber}
              helperText={errors.tanNumber}
              onChange={e => handleChange('tanNumber', e.target.value)}
              onBlur={e => handleFieldBlur('tanNumber', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? 'Tax year*' : 'Financial Year*'}
            </Typography>

            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={true}
                value={formData.financialYear}
                onChange={e => handleChange('financialYear', e.target.value)}
                onBlur={e => handleFieldBlur('financialYear', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
                slotProps={{
                  formHelperText: {
                    sx: { margin: 0 },
                  },
                  htmlInput: { maxLength: 6 },
                }}
              >
                {yearOptions.map(item => (
                  <MenuItem value={item.value}>{item.label}</MenuItem>
                ))}
              </Select>
              {!!errors.financialYear && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.financialYear}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Last Tax Deduction and Collection Account No.
            </Typography>
            <TextField
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: '#f5f5f5',
                  height: '40px',
                },
              }}
              disabled={true}
              value={formData.lastTan}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Assessment Year*
            </Typography>
            <TextField
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: '#f5f5f5',
                  height: '40px',
                },
              }}
              disabled={true}
              value={formData.assessmentYear}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Permanent Account Number*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.pan}
              error={!!errors.pan}
              helperText={errors.pan}
              onChange={e => handleChange('pan', e.target.value.toUpperCase())}
              onBlur={e => handleFieldBlur('pan', e.target.value.toUpperCase())}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          {!isNewForm && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Is this a Revised Return?*
            </Typography>

            <TextField
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: '#f5f5f5',
                  height: '40px',
                },
              }}
              disabled={true}
              value={formData.revisedReturn}
              error={!!(touched.revisedReturn && errors.revisedReturn)}
              helperText={touched.revisedReturn && errors.revisedReturn}
              onChange={e => handleChange('revisedReturn', e.target.value)}
              onBlur={e => handleFieldBlur('revisedReturn', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              For Quarter Ended*
            </Typography>

            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles(!editMode || !formData?.financialYear || !quarterOptions.length)}
                disabled={
                  !editMode ||
                  !formData?.financialYear ||
                  !quarterOptions.length
                }
                value={formData.quarterEnded}
                error={!!errors.quarterEnded}
                helperText={errors.quarterEnded}
                onChange={e => handleChange('quarterEnded', e.target.value)}
                onBlur={e => handleFieldBlur('quarterEnded', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {quarterOptions.map(val => (
                  <MenuItem key={val.value} value={val.value}>
                    {val.label}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.quarterEnded && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.quarterEnded}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Type of Deductor*
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={!editMode}
                value={formData.deductorType || ''}
                onChange={e => handleChange('deductorType', e.target.value)}
                onBlur={e => handleFieldBlur('deductorType', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {formHook.entityTypeOptions.map((entity, index) => (
                  <MenuItem key={index} value={entity}>
                    {entity}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.deductorType && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.deductorType}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Receipt Number of Previous Return
            </Typography>
            <TextField
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: '#f5f5f5',
                  height: '40px',
                },
              }}
              disabled={true}
              value={formData.previousReturnReceiptNo}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Last Deductor Type
            </Typography>
            <TextField
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: '#f5f5f5',
                  height: '40px',
                },
              }}
              disabled={true}
              value={formData.lastDeductorType}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Receipt Number of Original Return
            </Typography>
            <TextField
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: '#f5f5f5',
                  height: '40px',
                },
              }}
              disabled={true}
              value={formData.originalReturnReceiptNo}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Update Deductor Details
            </Typography>
            <TextField
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: '#f5f5f5',
                  height: '40px',
                },
              }}
              disabled={true}
              value={formData.updateDeductorDetails}
            />
          </Box>
        </Box>
      </Paper>

      {/* Particulars Of Deductor (Employer) */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontWeight: 600,
            fontSize: { xs: '16px', sm: '18px' },
          }}
        >
          2. Particulars Of Deductor (Employer)
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Name*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.name}
              error={!!errors.name}
              helperText={errors.name}
              onChange={e => handleChange('name', e.target.value)}
              onBlur={e => handleFieldBlur('name', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 75 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? 'Post Office' : 'Name of Premises/Building'}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.buildingName}
              error={!!(touched.buildingName && errors.buildingName)}
              helperText={touched.buildingName && errors.buildingName}
              onChange={e => handleChange('buildingName', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          {isNewForm && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Country/Region*
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={!editMode}
                value={formData.country || 'INDIA'}
                onChange={e => handleChange('country', e.target.value)}
                onBlur={e => handleFieldBlur('country', e.target.value)}
                 MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                      width: 'auto',
                      minWidth: '300px',
                    },
                  },
                }}
              >
                {COUNTRY_LIST.map((option, index) => (
                  <MenuItem key={index} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.country && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.country}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          )}
          {!isNewForm && (
          <Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Typography
                sx={{
                  minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                  fontSize: '14px',
                  fontWeight: { xs: 500, sm: 400 },
                }}
              >
                Branch/Division *
              </Typography>
              <TextField
                fullWidth
                sx={getTextFieldStyles()}
                disabled={!editMode}
                value={formData.branchOrDivision}
                error={!!errors.branchOrDivision}
                helperText={errors.branchOrDivision}
                onChange={e => handleChange('branchOrDivision', e.target.value)}
                onBlur={e =>
                  handleFieldBlur('branchOrDivision', e.target.value)
                }
                slotProps={{
                  formHelperText: {
                    sx: { margin: 0 },
                  },
                  htmlInput: { maxLength: 75 },
                }}
              />
            </Box>
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Area/Location')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.areaOrLocation}
              error={!!(touched.areaOrLocation && errors.areaOrLocation)}
              helperText={touched.areaOrLocation && errors.areaOrLocation}
              onChange={e => handleChange('areaOrLocation', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Flat/Door/Block No.*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.flatNo}
              error={!!errors.flatNo}
              helperText={errors.flatNo}
              onChange={e => handleChange('flatNo', e.target.value)}
              onBlur={e => handleFieldBlur('flatNo', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              State*
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={!editMode}
                value={formData.state || ''}
                onChange={e => handleChange('state', e.target.value)}
                onBlur={e => handleFieldBlur('state', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {formHook.stateOptions.map((state, index) => (
                  <MenuItem key={index} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.state && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.state}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Road/Street/Block/Sector
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.roadOrStreet}
              error={!!(touched.roadOrStreet && errors.roadOrStreet)}
              helperText={touched.roadOrStreet && errors.roadOrStreet}
              onChange={e => handleChange('roadOrStreet', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Pin Code*')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.pinCode}
              error={!!errors.pinCode}
              helperText={errors.pinCode}
              onChange={e => handleChange('pinCode', e.target.value)}
              onBlur={e => handleFieldBlur('pinCode', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 6 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Town/District/City')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.townDistrictCity}
              error={!!(touched.townDistrictCity && errors.townDistrictCity)}
              helperText={touched.townDistrictCity && errors.townDistrictCity}
              onChange={e => handleChange('townDistrictCity', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? 'Email ID*' : 'Email ID'}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.email}
              error={!!(isNewForm ? errors.email : touched.email && errors.email)}
              helperText={isNewForm ? errors.email : touched.email && errors.email}
              onChange={e => handleChange('email', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 75 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('STD Code')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.stdCode}
              error={!!(isNewForm ? errors.stdCode : touched.stdCode && errors.stdCode)}
              helperText={isNewForm ? errors.stdCode : touched.stdCode && errors.stdCode}
              onChange={e => handleChange('stdCode', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 5 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Telephone No.')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.telephoneNo}
              error={!!(errors.telephoneNo)}
              helperText={errors.telephoneNo}
              onChange={e => handleChange('telephoneNo', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          {!isHiddenField('alternateStdCode') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              STD Code (Alternate).
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.alternateStdCode}
              error={!!(touched.alternateStdCode && errors.alternateStdCode)}
              helperText={touched.alternateStdCode && errors.alternateStdCode}
              onChange={e => handleChange('alternateStdCode', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 5 },
              }}
            />
          </Box>
          )}
          {!isHiddenField('alternateTelephoneNo') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Telephone No (Alternate).
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.alternateTelephoneNo}
              error={
                !!(errors.alternateTelephoneNo)
              }
              helperText={
                errors.alternateTelephoneNo
              }
              onChange={e =>
                handleChange('alternateTelephoneNo', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          )}
          {!isHiddenField('alternateEmail') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Email ID (Alternate)
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.alternateEmail}
              error={!!(touched.alternateEmail && errors.alternateEmail)}
              helperText={touched.alternateEmail && errors.alternateEmail}
              onChange={e => handleChange('alternateEmail', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 75 },
              }}
            />
          </Box>
          )}
          {!isHiddenField('paoCode') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              PAO Code
            </Typography>
            <TextField
              fullWidth
              sx={getConditionalTextFieldStyles(!editMode || disabledFields.paoCode)}
              disabled={!editMode || disabledFields.paoCode}
              value={formData.paoCode}
              error={!!(errors.paoCode)}
              helperText={errors.paoCode}
              onChange={e =>
                editMode && handleChange('paoCode', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 20 },
              }}
            />
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Goods and Service Tax Number (GSTN)
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.gstn}
              error={!!(touched.gstn && errors.gstn)}
              helperText={touched.gstn && errors.gstn}
              onChange={e => handleChange('gstn', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 15 },
              }}
            />
          </Box>
          {!isHiddenField('ddoCode') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              DDO Code
            </Typography>
            <TextField
              fullWidth
              sx={getConditionalTextFieldStyles(!editMode || disabledFields.ddoCode)}
              value={formData.ddoCode}
              disabled={!editMode || disabledFields.ddoCode}
              error={!!(errors.ddoCode)}
              helperText={errors.ddoCode}
              onChange={e => handleChange('ddoCode', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 20 },
              }}
            />
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? "Ministry / Dept.Name" : "Ministry Name"}
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles(!editMode || disabledFields.ministryName)}
                disabled={!editMode || disabledFields.ministryName}
                value={formData.ministryName}
                error={!!(touched.ministryName && errors.ministryName)}
                helperText={touched.ministryName && errors.ministryName}
                onChange={e => handleChange('ministryName', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {formHook.ministryOptions.map((ministry, index) => (
                  <MenuItem key={index} value={ministry}>
                    {ministry}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.ministryName && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.ministryName}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          {!isHiddenField('paoRegistrationNo') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              PAO Registration No.
            </Typography>
            <TextField
              fullWidth
              sx={getConditionalTextFieldStyles(!editMode || disabledFields.paoRegistrationNo)}
              disabled={!editMode || disabledFields.paoRegistrationNo}
              value={formData.paoRegistrationNo}
              error={!!(touched.paoRegistrationNo && errors.paoRegistrationNo)}
              helperText={touched.paoRegistrationNo && errors.paoRegistrationNo}
              onChange={e => handleChange('paoRegistrationNo', e.target.value)}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 7 },
              }}
            />
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? "Ministry / Dept.Name others" :"Ministry Name Other"}
            </Typography>
            <TextField
              fullWidth
              sx={getConditionalTextFieldStyles(
                !editMode ||
                disabledFields.ministryNameOther ||
                formData.ministryName !== 'OTHERS'
              )}
              disabled={
                !editMode ||
                disabledFields.ministryNameOther ||
                formData.ministryName !== 'OTHERS'
              }
              error={!!(errors.ministryNameOther)}
              helperText={errors.ministryNameOther}
              value={formData.ministryNameOther}
              onChange={e => handleChange('ministryNameOther', e.target.value)}
            />
          </Box>
          {!isHiddenField('ddoRegistrationNo') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              DDO Registration No.
            </Typography>
            <TextField
              fullWidth
              sx={getConditionalTextFieldStyles(!editMode || disabledFields.ddoRegistrationNumber)}
              value={formData.ddoRegistrationNo}
              error={!!(touched.ddoRegistrationNo && errors.ddoRegistrationNo)}
              helperText={touched.ddoRegistrationNo && errors.ddoRegistrationNo}
              onChange={e => handleChange('ddoRegistrationNo', e.target.value)}
              disabled={!editMode || disabledFields.ddoRegistrationNumber}
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 20 },
              }}
            />
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              State Name
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles(!editMode || disabledFields.stateName)}
                error={!!(touched.stateName && errors.stateName)}
                helperText={touched.stateName && errors.stateName}
                disabled={!editMode || disabledFields.stateName}
                value={formData.stateName}
                onChange={e => handleChange('stateName', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {formHook.stateOptions.map((state, index) => (
                  <MenuItem key={index} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
              {errors.stateName && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.stateName}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          {!isNewForm && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Has Address changed since Last Return*
            </Typography>

            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={!editMode}
                value={formData.addressChangedSinceLastReturn}
                error={!!errors.addressChangedSinceLastReturn}
                helperText={errors.addressChangedSinceLastReturn}
                onChange={e =>
                  handleChange('addressChangedSinceLastReturn', e.target.value)
                }
                onBlur={e =>
                  handleFieldBlur(
                    'addressChangedSinceLastReturn',
                    e.target.value
                  )
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                <MenuItem value={'Yes'}>Yes</MenuItem>
                <MenuItem value={'No'}>No</MenuItem>
              </Select>
              {!!errors.addressChangedSinceLastReturn && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.addressChangedSinceLastReturn}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Account Office Identification Number (AIN) of PAO/TO/CDDO
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.accountOfficeIdNumber}
              error={
                !!(
                  touched.accountOfficeIdNumber && errors.accountOfficeIdNumber
                )
              }
              helperText={
                touched.accountOfficeIdNumber && errors.accountOfficeIdNumber
              }
              onChange={e =>
                handleChange('accountOfficeIdNumber', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 75 },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Particulars Of The Person Responsible For Deduction Of Tax */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontWeight: 600,
            fontSize: { xs: '16px', sm: '18px' },
          }}
        >
          3. Particulars Of The Person Responsible For Deduction Of Tax
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Name*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonName}
              error={!!errors.responsiblePersonName}
              helperText={errors.responsiblePersonName}
              onChange={e =>
                handleChange('responsiblePersonName', e.target.value)
              }
              onBlur={e =>
                handleFieldBlur('responsiblePersonName', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 75 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Permanent Account Number*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonPAN}
              error={!!errors.responsiblePersonPAN}
              helperText={errors.responsiblePersonPAN}
              onChange={e =>
                handleChange('responsiblePersonPAN', e.target.value.toUpperCase())
              }
              onBlur={e =>
                handleFieldBlur('responsiblePersonPAN', e.target.value.toUpperCase())
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? 'Post Office' : 'Name of Premises/Building'}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonBuilding}
              error={
                !!(
                  touched.responsiblePersonBuilding &&
                  errors.responsiblePersonBuilding
                )
              }
              helperText={
                touched.responsiblePersonBuilding &&
                errors.responsiblePersonBuilding
              }
              onChange={e =>
                handleChange('responsiblePersonBuilding', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Designation*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonDesignation}
              error={!!errors.responsiblePersonDesignation}
              helperText={errors.responsiblePersonDesignation}
              onChange={e =>
                handleChange('responsiblePersonDesignation', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 20 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Area/Location')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonArea}
              error={
                !!(
                  touched.responsiblePersonArea && errors.responsiblePersonArea
                )
              }
              helperText={
                touched.responsiblePersonArea && errors.responsiblePersonArea
              }
              onChange={e =>
                handleChange('responsiblePersonArea', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Flat/Door/Block No.*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonFlat}
              error={!!errors.responsiblePersonFlat}
              helperText={errors.responsiblePersonFlat}
              onChange={e =>
                handleChange('responsiblePersonFlat', e.target.value)
              }
              onBlur={e =>
                handleFieldBlur('responsiblePersonFlat', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              State*
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={!editMode}
                value={formData.responsiblePersonState}
                error={
                  !!(
                    touched.responsiblePersonState &&
                    errors.responsiblePersonState
                  )
                }
                helperText={
                  touched.responsiblePersonState &&
                  errors.responsiblePersonState
                }
                onChange={e =>
                  handleChange('responsiblePersonState', e.target.value)
                }
                onBlur={e =>
                  handleFieldBlur('responsiblePersonState', e.target.value)
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {formHook.stateOptions.map((state, index) => (
                  <MenuItem key={index} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.responsiblePersonState && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.responsiblePersonState}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Road/Street/Block/Sector
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonStreet}
              error={
                !!(
                  touched.responsiblePersonStreet &&
                  errors.responsiblePersonStreet
                )
              }
              helperText={
                touched.responsiblePersonStreet &&
                errors.responsiblePersonStreet
              }
              onChange={e =>
                handleChange('responsiblePersonStreet', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Pin Code*')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonPIN}
              error={!!errors.responsiblePersonPIN}
              helperText={errors.responsiblePersonPIN}
              onChange={e =>
                handleChange('responsiblePersonPIN', e.target.value)
              }
              onBlur={e =>
                handleFieldBlur('responsiblePersonPIN', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 6 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Town/District/City')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonCity}
              error={
                !!(
                  touched.responsiblePersonCity && errors.responsiblePersonCity
                )
              }
              helperText={
                touched.responsiblePersonCity && errors.responsiblePersonCity
              }
              onChange={e =>
                handleChange('responsiblePersonCity', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? 'Email ID*' : 'Email ID'}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonEmail}
              error={
                !!(
                  isNewForm
                    ? errors.responsiblePersonEmail
                    : touched.responsiblePersonEmail &&
                      errors.responsiblePersonEmail
                )
              }
              helperText={
                isNewForm
                  ? errors.responsiblePersonEmail
                  : touched.responsiblePersonEmail && errors.responsiblePersonEmail
              }
              onChange={e =>
                handleChange('responsiblePersonEmail', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 75 },
              }}
            />
          </Box>
          {!isHiddenField("responsiblePersonEmailAlt") &&<Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Email ID (Alternate)
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonEmailAlt}
              error={
                !!(
                  touched.responsiblePersonEmailAlt &&
                  errors.responsiblePersonEmailAlt
                )
              }
              helperText={
                touched.responsiblePersonEmailAlt &&
                errors.responsiblePersonEmailAlt
              }
              onChange={e =>
                handleChange('responsiblePersonEmailAlt', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 75 },
              }}
            />
          </Box>}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('STD Code')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              error={
                !!(isNewForm ? errors.responsiblePersonSTD : touched.responsiblePersonSTD && errors.responsiblePersonSTD)
              }
              value={formData.responsiblePersonSTD}
              helperText={
                isNewForm ? errors.responsiblePersonSTD : touched.responsiblePersonSTD && errors.responsiblePersonSTD
              }
              onChange={e =>
                handleChange('responsiblePersonSTD', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 5 },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {getLabel('Telephone No.')}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonTelephone}
              error={
                !!(
                  isNewForm
                    ? errors.responsiblePersonTelephone
                    : touched.responsiblePersonTelephone &&
                      errors.responsiblePersonTelephone
                )
              }
              helperText={
                isNewForm
                  ? errors.responsiblePersonTelephone
                  : touched.responsiblePersonTelephone &&
                    errors.responsiblePersonTelephone
              }
              onChange={e =>
                handleChange('responsiblePersonTelephone', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          {!isHiddenField('responsiblePersonAlternateStdCode') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              STD Code (Alternate)
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonSTDAlt}
              error={
                !!(
                  touched.responsiblePersonSTDAlt &&
                  errors.responsiblePersonSTDAlt
                )
              }
              helperText={
                touched.responsiblePersonSTDAlt &&
                errors.responsiblePersonSTDAlt
              }
              onChange={e =>
                handleChange('responsiblePersonSTDAlt', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 5 },
              }}
            />
          </Box>
          )}
          {!isHiddenField('responsiblePersonAlternateTelephoneNo') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Telephone No. (Alternate)
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonTelephoneAlt}
              error={
                !!(
                  touched.responsiblePersonTelephoneAlt &&
                  errors.responsiblePersonTelephoneAlt
                )
              }
              helperText={
                touched.responsiblePersonTelephoneAlt &&
                errors.responsiblePersonTelephoneAlt
              }
              onChange={e =>
                handleChange('responsiblePersonTelephoneAlt', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          )}
          {!isHiddenField('addressChangedSinceLastReturnRP') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Has Address changed since Last Return*
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={!editMode}
                error={!!errors.responsiblePersonAddressChanged}
                helperText={errors.responsiblePersonAddressChanged}
                value={formData.responsiblePersonAddressChanged}
                onChange={e =>
                  handleChange(
                    'responsiblePersonAddressChanged',
                    e.target.value
                  )
                }
                onBlur={e =>
                  handleFieldBlur(
                    'responsiblePersonAddressChanged',
                    e.target.value
                  )
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                <MenuItem value={'Yes'}>Yes</MenuItem>
                <MenuItem value={'No'}>No</MenuItem>
              </Select>
              {!!errors.responsiblePersonAddressChanged && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.responsiblePersonAddressChanged}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          )}
          {!isHiddenField('responsiblePersonMobileNo') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Mobile No.*
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonMobile}
              error={!!errors.responsiblePersonMobile}
              helperText={errors.responsiblePersonMobile}
              onChange={e =>
                handleChange('responsiblePersonMobile', e.target.value)
              }
              onBlur={e =>
                handleFieldBlur('responsiblePersonMobile', e.target.value)
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
          )}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? 'Has the statement been filled earlier for this Quarter*' : `Has regular statement filed for Form ${formData?.formType} for earlier period*`}
            </Typography>

            <FormControl fullWidth sx={{ maxWidth: '411px' }}>
              <Select
                sx={getSelectStyles()}
                disabled={!editMode}
                value={formData.responsiblePersonRegularStatement}
                onChange={e =>
                  handleChange(
                    'responsiblePersonRegularStatement',
                    e.target.value
                  )
                }
                onBlur={e =>
                  handleFieldBlur(
                    'responsiblePersonRegularStatement',
                    e.target.value
                  )
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                <MenuItem value={'Yes'}>Yes</MenuItem>
                <MenuItem value={'No'}>No</MenuItem>
              </Select>
              {!!errors.responsiblePersonRegularStatement && (
                <FormHelperText className="!text-red-500" sx={{ margin: 0 }}>
                  {errors.responsiblePersonRegularStatement}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                fontSize: '14px',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              {isNewForm ? 'Receipt Number of Original Return*' : `Receipt No. of earlier statement filed for Form ${formData?.formType}*`}
            </Typography>
            <TextField
              fullWidth
              sx={getTextFieldStyles()}
              disabled={!editMode}
              value={formData.responsiblePersonReceiptNumber}
              error={!!errors.responsiblePersonReceiptNumber}
              helperText={errors.responsiblePersonReceiptNumber}
              onChange={e =>
                handleChange('responsiblePersonReceiptNumber', e.target.value)
              }
              onBlur={e =>
                handleFieldBlur(
                  'responsiblePersonReceiptNumber',
                  e.target.value
                )
              }
              slotProps={{
                formHelperText: {
                  sx: { margin: 0 },
                },
                htmlInput: { maxLength: 15 },
              }}
            />
          </Box>
        </Box>
      </Paper>
    </>
  )
}

export default ReturnsForm
